'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export const CIV_ID_STORAGE_KEY = 'dealflow_selected_civ_id';

type SelectedCivContextType = {
  selectedCivId: string;
  setSelectedCivId: (id: string) => void;
};

const SelectedCivContext = createContext<SelectedCivContextType | undefined>(undefined);

// Vérifie si on est côté navigateur
const isBrowser = typeof window !== 'undefined';

export function SelectedCivProvider({ children }: { children: React.ReactNode }) {
  const [selectedCivId, setSelectedCivId] = useState<string>(() => {
    // Initialiser avec une valeur par défaut ou depuis le localStorage
    if (isBrowser) {
      return localStorage.getItem(CIV_ID_STORAGE_KEY) || '';
    }
    return '';
  });

  // Persist to localStorage when selectedCivId changes
  useEffect(() => {
    if (isBrowser && selectedCivId) {
      localStorage.setItem(CIV_ID_STORAGE_KEY, selectedCivId);
    }
  }, [selectedCivId]);

  // Mettre à jour l'état si le localStorage change depuis un autre onglet
  useEffect(() => {
    if (!isBrowser) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CIV_ID_STORAGE_KEY && e.newValue !== selectedCivId) {
        setSelectedCivId(e.newValue || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [selectedCivId]);

  return (
    <SelectedCivContext.Provider value={{ selectedCivId, setSelectedCivId }}>
      {children}
    </SelectedCivContext.Provider>
  );
}

export function useSelectedCiv() {
  const context = useContext(SelectedCivContext);
  if (context === undefined) {
    throw new Error('useSelectedCiv must be used within a SelectedCivProvider');
  }
  return context;
}
